import type { ReactNode } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
  /** Rendered as the card title on mobile. */
  title?: boolean;
  /** Hidden from the mobile card (still shown in the desktop table). */
  secondary?: boolean;
}

interface ResponsiveTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  loadingLabel?: string;
  emptyLabel?: string;
  actions?: (row: T) => ReactNode;
  /** Placeholder rows shown while loading (defaults to 8). */
  skeletonRows?: number;
}

const BAR_WIDTHS = ['w-32', 'w-20', 'w-16', 'w-24', 'w-20', 'w-16'];

function SkeletonBar({ className }: { className?: string }) {
  return <div className={cn('h-5 animate-pulse rounded bg-muted', className)} />;
}

function skeletonKeys(count: number): string[] {
  return Array.from({ length: count }, (_, index) => `skeleton-${index}`);
}

/**
 * Table on >= sm, stacked cards on smaller screens. Keeps every list usable on
 * mobile without horizontal scrolling. While loading it renders a full page of
 * skeleton rows so the layout doesn't shift.
 */
export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  loadingLabel = 'Loading…',
  emptyLabel = 'No results.',
  actions,
  skeletonRows = 8,
}: ResponsiveTableProps<T>) {
  const titleColumn = columns.find((column) => column.title) ?? columns[0];
  const detailColumns = columns.filter((column) => column !== titleColumn && !column.secondary);
  const showSkeleton = loading && rows.length === 0;
  const keys = skeletonKeys(skeletonRows);

  return (
    <>
      {showSkeleton && <span className="sr-only">{loadingLabel}</span>}

      <div className="hidden sm:block">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(column.align === 'right' && 'text-right', column.className)}
                >
                  {column.header}
                </TableHead>
              ))}
              {actions && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={showSkeleton}>
            {showSkeleton ? (
              keys.map((key) => (
                <TableRow key={key}>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column.key}
                      className={cn(column.align === 'right' && 'text-right', column.className)}
                    >
                      <SkeletonBar
                        className={cn(
                          column.title ? 'w-40' : (BAR_WIDTHS[index % BAR_WIDTHS.length] ?? 'w-24'),
                          column.align === 'right' && 'ml-auto',
                        )}
                      />
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell className="text-right">
                      {/* Match the 32px icon-button so the row height is identical. */}
                      <SkeletonBar className="ml-auto size-8 rounded-md" />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyLabel}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={rowKey(row)}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={cn(column.align === 'right' && 'text-right', column.className)}
                    >
                      {column.cell(row)}
                    </TableCell>
                  ))}
                  {actions && <TableCell className="text-right">{actions(row)}</TableCell>}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-3 sm:hidden">
        {showSkeleton ? (
          keys.map((key) => (
            <div key={key} className="rounded-md border bg-card p-3">
              <div className="flex items-start justify-between gap-3">
                <SkeletonBar className="w-40" />
                {actions && <SkeletonBar className="w-6" />}
              </div>
              <div className="mt-3 space-y-2">
                <SkeletonBar className="w-24" />
                <SkeletonBar className="w-28" />
              </div>
            </div>
          ))
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground text-sm">{emptyLabel}</p>
        ) : (
          rows.map((row) => (
            <div key={rowKey(row)} className="rounded-md border bg-card p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 font-medium">{titleColumn.cell(row)}</div>
                {actions && <div className="shrink-0">{actions(row)}</div>}
              </div>
              {detailColumns.length > 0 && (
                <dl className="mt-2 space-y-1">
                  {detailColumns.map((column) => (
                    <div key={column.key} className="flex items-center justify-between gap-3">
                      <dt className="text-muted-foreground text-xs">{column.header}</dt>
                      <dd className={cn('text-sm', column.align === 'right' && 'text-right')}>
                        {column.cell(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
}
